<script setup lang="ts">
  import { onMounted, ref } from 'vue'
  import { useMenuStore } from '@/stores/menu'
  import { useUserStore } from '@/stores/user'
  import { useClientStore } from '@/stores/client'
  import { getAgencyFromUrl, getAgencyInfo, redirectToAgency } from '@/helpers/agency'
  import type { TAgency } from '@/types/agency'
  import DesNavigationPanel from '@/components/navigation/DesNavigationPanel.vue'
  import DesNavigation from '@/components/navigation/DesNavigation.vue'
  import DesNavigationItem from '@/components/navigation/DesNavigationItem.vue'
  import DesTooltip from '@/components/tips/DesTooltip.vue'
  import DesPopup from '@/components/DesPopup.vue'
  import DesModal from '@/components/modal/DesModal.vue'
  import DesLoader from '@/components/DesLoader.vue'
  import DesSnackbar from '@/components/DesSnackbar.vue'
  import DesTopBars from '@/components/bar/DesTopBars.vue'
  import DesTour from '@/components/tips/DesTour.vue'
  import DesMenu from '@/components/menu/DesMenu.vue'
  import DesMenuItem from '@/components/menu/DesMenuItem.vue'
  import DesMenuSeparator from '@/components/menu/DesMenuSeparator.vue'
  import DesBarMenuUser from '@/components/bar/DesBarMenuUser.vue'
  import DesAdvice from '@/components/tips/DesAdvice.vue'
  import DesBarScreenCastomatic from '@/components/bar/DesBarScreenCastomatic.vue'
  import DesButtonIconLite from '@/components/button/DesButtonIconLite.vue'

  const user = useUserStore()
  const client = useClientStore()
  const menu = useMenuStore()
  const logo = ref<string>('//app.designrr.io/assets/images/logo.png')
  const agency = ref<TAgency | null>(null)

  async function logout() {
    await client.performLogout()
    user.logout()
  }

  onMounted(async () => {
    client.getDashboard()
    const agencySlug = getAgencyFromUrl()

    if (agencySlug) {
      const agencyRes = await getAgencyInfo(agencySlug)

      if (agencyRes.result) {
        agency.value = agencyRes.agency
        logo.value = agencyRes.agency.logo ?? logo.value
      } else if ('redirect' in agencyRes && agencyRes?.redirect) {
        redirectToAgency(agencyRes)
      }
    }
  })
</script>

<template>
  <div class="relative min-h-screen text-blue-10">
    <DesTopBars alert-only />
    <header
      class="sticky ml-0 top-0 flex items-center justify-between h-16 pr-5 pl-6 border-b border-white-gray bg-white z-50 gap-6"
      :class="{ 'md:ml-navigation': menu.isOpen }"
    >
      <DesBarScreenCastomatic />
      <DesButtonIconLite
        @click="menu.isOpen = !menu.isOpen"
        label="Toggle navigation"
        icon="fa-light fa-sidebar"
        :class="{ 'text-blue-50': menu.isOpen }"
      />

      <slot name="search"></slot>
      <div></div>
    </header>

    <DesNavigationPanel>
      <RouterLink to="/" class="shrink-0 h-16 hidden md:flex items-center" :class="{ 'max-w-navigation': menu.isOpen }">
        <img
          class="h-6 mx-5 object-contain"
          alt=""
          v-if="logo"
          :class="{ 'max-w-navigation': menu.isOpen }"
          :src="logo"
        />
      </RouterLink>

      <DesNavigation>
        <DesNavigationItem icon="fa-light fa-folder" name="My projects" to="/client" />
      </DesNavigation>
      <div class="grow"></div>
      <DesNavigation>
        <DesMenuSeparator />
        <DesMenu right force-flip>
          <template #toggle>
            <DesBarMenuUser />
          </template>
          <DesMenuItem to="/client/account" icon="fa-light fa-user">Account settings</DesMenuItem>
          <DesMenuSeparator />
          <DesMenuItem icon="fa-light fa-arrow-right-from-bracket" @click="logout()">Log out</DesMenuItem>
        </DesMenu>
      </DesNavigation>
    </DesNavigationPanel>

    <main class="px-6 py-9 bg-white" :class="{ 'panel-margin': menu.isOpen }">
      <slot name="main"></slot>
    </main>

    <!--disable feedback now for DES-3780-->
    <!--<DesFeedback />-->
    <DesModal />
    <DesPopup />
    <DesTooltip />
    <DesLoader />
    <DesSnackbar />
    <DesAdvice />
    <DesTour />
  </div>
</template>
